import { useState } from 'react'

import type { Media } from '../../api/types'
import { dauerText, kannAbspielen } from '../../lib/video'
import { groesse } from '../../lib/format'

/**
 * Ein Video im Verlauf.
 *
 * Zwei Fälle, und der zweite ist der Grund, warum es diese Datei gibt:
 *
 * 1. **Der Browser kann den Typ.** Dann steht hier ein Player mit Standbild.
 *    Wie viel er im Voraus holt, steht am `preload` unten und ist dort
 *    begründet.
 * 2. **Der Browser kann den Typ nicht.** Ein iPhone nimmt in HEVC auf; Chrome
 *    und Firefox spielen das nicht ab. Statt eines Players, der schwarz
 *    bleibt und keine Erklärung gibt, steht dann das Standbild da, ein Satz
 *    dazu und ein Weg zur Datei.
 *
 * Umgewandelt wird nichts: ohne ffmpeg kann der Server es nicht, und im
 * Browser dauerte es Minuten je Video.
 */
/**
 * **Kein Antwortknopf mehr am Video.**
 *
 * Hier stand „Auf dieses Video antworten" als Pille über dem Player. Aus dem
 * Betrieb: „außerdem kann dann bei Videos der Knopf ‚auf dieses Video
 * antworten' entfallen" -- seit das Menü beim langen Drücken (#8) ein
 * *Antworten* trägt, war er ein zweiter Weg zu derselben Sache, und einer,
 * der als einziger Knopf dauerhaft über einem Bild lag.
 *
 * Die Kennung des Videos geht dabei nicht verloren: das Menü gibt sie mit,
 * wenn genau ein Anhang an der Nachricht hängt (`einzelnesMedium` in
 * `Bubble.tsx`), und ein Video liegt praktisch immer allein.
 */
export function VideoPlayer({ media }: { media: Media }) {
  const [gestartet, setGestartet] = useState(false)
  // Die Form des Kastens kommt vom Video selbst, sobald es sie kennt.
  //
  // Vorher stand hier `width`/`height` aus der Datenbank -- also eine
  // Behauptung über die Form, die niemand geprüft hatte. Eine iPhone-Aufnahme
  // liegt in der Datei immer quer und trägt die Drehung als Marke daneben; ob
  // ein Browser die gedrehte oder die rohe Größe meldet, ist von Fassung zu
  // Fassung verschieden. Ein Querformat-Video steckte deshalb in einem
  // hochkanten Rahmen mit schwarzen Balken.
  const [form, setForm] = useState<number | null>(
    media.width > 0 && media.height > 0 ? media.width / media.height : null,
  )
  const abspielbar = kannAbspielen(media.mime)

  // Getrennt aufgesammelt statt in der Zeile zusammengesetzt: was es nicht
  // gibt, soll auch keinen Trenner hinterlassen.
  const steckbrief = [
    media.duration_ms > 0 ? dauerText(media.duration_ms) : '',
    // Die **kurze** Kante: eine Hochkantaufnahme misst 1080 x 1920, und
    // „1920p" stünde dort falsch.
    media.width > 0 && media.height > 0 ? `${Math.min(media.width, media.height)}p` : '',
    media.bytes > 0 ? groesse(media.bytes) : '',
  ]
    .filter(Boolean)
    .join(' · ')

  if (!abspielbar) {
    return (
      <div className="video video--fremd">
        {media.poster_url && <img className="video__poster" src={media.poster_url} alt="" />}
        <p className="video__hinweis">
          Dieses Video ist in einem Format, das dieser Browser nicht abspielt
          (meist HEVC vom iPhone). Auf einem iPhone, iPad oder Mac läuft es.
        </p>
        {/* Bewusst ein Link und kein Download-Attribut: der Browser darf
            selbst entscheiden, ob er es speichert oder in einem eigenen Tab
            versucht -- manche können es dort doch. */}
        <a className="button button--outline" href={media.url} target="_blank" rel="noreferrer">
          Video öffnen{media.duration_ms > 0 ? ` (${dauerText(media.duration_ms)})` : ''}
        </a>
      </div>
    )
  }

  return (
    <div className="video">
      <video
        className="video__player"
        // Ohne Standbild ein Ausweg, der ohne uns auskommt: `#t=0.1` sagt dem
        // Player, welche Stelle er zeigen soll. Er holt sich genau diesen
        // Ausschnitt über den Bereichsabruf -- und zeigt ein Bild statt einer
        // schwarzen Fläche, auch wenn das Standbild nie entstanden ist.
        src={media.poster_url === null ? `${media.url}#t=0.1` : media.url}
        poster={media.poster_url ?? undefined}
        controls
        // Ohne `playsInline` reisst iOS das Video in den Vollbildplayer, und
        // man ist aus dem Verlauf heraus.
        playsInline
        // Der Browser darf vorpuffern, und das ist der Punkt: aus dem Betrieb
        // kam „Videos ruckeln beim Abspielen“. Ein Video hat ein Ende --
        // anders als ein Live-Strom lädt der Player weit voraus und spielt
        // dann glatt, auch wenn die Leitung schmaler ist als die Datenrate.
        //
        // Der Preis gehört dazu: vorher stand hier `metadata`, damit nicht
        // zehn Videos in einem geöffneten Verlauf gleichzeitig anfangen zu
        // laden. `auto` ist eine Bitte, kein Befehl -- Safari und Chrome
        // halten sich am Mobilfunknetz von sich aus zurück. Ob der Handel
        // aufgeht, sagt das Gerät; die Rücknahme wäre diese eine Zeile.
        //
        // Die wahre Form kennt der Browser weiterhin **vor** dem Antippen --
        // `auto` schließt den Kopf der Datei ein, `onLoadedMetadata` kommt
        // also nicht später als zuvor.
        preload="auto"
        // Der gespeicherte Wert hält nur den Platz frei, damit der Verlauf
        // beim Laden nicht springt. Was gilt, sagt das Video selbst.
        style={form === null ? undefined : { aspectRatio: String(form) }}
        onLoadedMetadata={(event) => {
          const el = event.currentTarget
          if (el.videoWidth > 0 && el.videoHeight > 0) {
            setForm(el.videoWidth / el.videoHeight)
          }
        }}
        onPlay={() => setGestartet(true)}
      />
      {/* **Was da liegt, bevor man tippt.** Länge, Höhe und Gewicht — die
          Angaben kommen vom Server und beschreiben die **abgelegte** Datei,
          nicht die, die jemand ausgewählt hat. Wer wissen will, ob das
          Verkleinern gewirkt hat, sieht es hier auch Tage später noch.

          Nur vor dem Abspielen: währenddessen gehört die Ecke dem Player. */}
      {!gestartet && steckbrief !== '' && <span className="video__dauer">{steckbrief}</span>}
    </div>
  )
}
